import { decrypt } from '@/common/securityUtils';
import { nameValidationRegex } from '@/constants/regex';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { z } from 'zod';

export const AffiliateFormSchema = z
  .object({
    userName: z.string(),
    fullName: z
      .string()
      .trim()
      .min(1, 'common.form.validation.required')
      .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
    affiliateNameFurigana: z
      .string()
      .trim()
      .min(1, 'common.form.validation.required')
      .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
    mobileNumber: z.string().min(1, 'common.form.validation.required'),
    emailAddress: z.string().min(1, 'common.form.validation.required'),
    dob: z
      .string()
      .min(1, 'common.form.validation.required')
      .refine(
        (dob) => {
          const birthDate = new Date(dob);
          const today = new Date();

          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }

          return age >= 18;
        },
        {
          message: 'common.form.validation.ageValidation',
        },
      ),

    gender: z.enum(['M', 'F', 'O']).optional(),
    image: optionalUploadValueSchema,
    postalCode: z
      .string()
      .min(8, 'common.form.validation.invalidPostalCode')
      .max(8, 'common.form.validation.invalidPostalCode')
      .transform((val) => val.replace(/\D/g, ''))
      .transform((val) => `${val.slice(0, 3)}-${val.slice(3)}`),
    prefecture: z
      .string()
      .min(1, 'common.form.validation.required')
      .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
    city: z
      .string()
      .min(1, 'common.form.validation.required')
      .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
    street: z
      .string()
      .min(1, 'common.form.validation.required')
      .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
    buildingNumber: z
      .string()
      .refine((val) => val === '' || nameValidationRegex.test(val), {
        message: 'common.form.validation.nameOnly',
      })
      .optional(),
    businessType: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    ceoName: z
      .string()
      .trim()
      .refine((val) => val === '' || nameValidationRegex.test(val), {
        message: 'common.form.validation.nameOnly',
      })
      .optional(),
    ceoNameFurigana: z
      .string()
      .trim()
      .refine((val) => val === '' || nameValidationRegex.test(val), {
        message: 'common.form.validation.nameOnly',
      })
      .optional(),
    companyName: z
      .string()
      .trim()
      .refine((val) => val === '' || nameValidationRegex.test(val), {
        message: 'common.form.validation.nameOnly',
      })
      .optional(),
    companyAddress: z
      .string()
      .trim()
      .refine((val) => val === '' || nameValidationRegex.test(val), {
        message: 'common.form.validation.nameOnly',
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (decrypt(data.businessType) === '1') {
      const ceoName = data.ceoName?.trim();

      if (!ceoName) {
        ctx.addIssue({
          path: ['ceoName'],
          message: 'common.form.validation.required',
          code: z.ZodIssueCode.custom,
        });
      }

      const ceoFurigana = data.ceoNameFurigana?.trim();
      if (!ceoFurigana) {
        ctx.addIssue({
          path: ['ceoNameFurigana'],
          message: 'common.form.validation.required',
          code: z.ZodIssueCode.custom,
        });
      }

      const companyName = data.companyName?.trim();
      if (!companyName) {
        ctx.addIssue({
          path: ['companyName'],
          message: 'common.form.validation.required',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });
